import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Input, DateField } from '@structural-climbing/ui'
import type { CompetitionSession } from './types'

interface Props {
  competitionId: string
  /** The competition window; a group has to sit inside it. */
  startsAt: string
  endsAt: string
  sessions: CompetitionSession[]
  /** The competition is over, so its shape is the result and cannot change. */
  locked: boolean
  onRefresh: () => void
}

interface Draft {
  name: string
  starts_at: string
  ends_at: string
  capacity: string
}

const EMPTY: Draft = { name: '', starts_at: '', ends_at: '', capacity: '' }

/** Trims a stored wall-clock value to the minute-precision value a DateField takes. */
function bound(value: string): string {
  return value.slice(0, 16)
}

export function SessionsTab({
  competitionId,
  startsAt,
  endsAt,
  sessions,
  locked,
  onRefresh,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [draft, setDraft] = useState<Draft>({
    ...EMPTY,
    // Opening on the competition's own window makes the common case one tap.
    starts_at: bound(startsAt),
    ends_at: bound(endsAt),
  })
  const [editingId, setEditingId] = useState<string | null>(null)
  // The add form stays out of the way until asked for, once there is a list.
  const [formOpen, setFormOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function resetForm() {
    setDraft({ ...EMPTY, starts_at: bound(startsAt), ends_at: bound(endsAt) })
    setEditingId(null)
    setFormOpen(false)
    setError(null)
  }

  function startEditing(session: CompetitionSession) {
    setDraft({
      name: session.name,
      starts_at: bound(session.starts_at),
      ends_at: bound(session.ends_at),
      capacity: session.capacity === null ? '' : String(session.capacity),
    })
    setEditingId(session.id)
    setError(null)
  }

  async function saveSession() {
    if (!draft.name.trim() || !draft.starts_at || !draft.ends_at) {
      setError(t('competitions.errorGroupFields'))
      return
    }
    if (draft.ends_at <= draft.starts_at) {
      setError(t('competitions.errorEndBeforeStart'))
      return
    }
    if (draft.starts_at < bound(startsAt) || draft.ends_at > bound(endsAt)) {
      setError(t('competitions.errorGroupOutsideWindow'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const fields = {
        name: draft.name.trim(),
        starts_at: draft.starts_at,
        ends_at: draft.ends_at,
        capacity: draft.capacity ? Number(draft.capacity) : null,
      }
      // Editing keeps the group's place in the list, so sort_order is left alone.
      const res = editingId
        ? await fetch(`${apiUrl}/competition-sessions/${editingId}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(fields),
          })
        : await fetch(`${apiUrl}/competitions/${competitionId}/sessions`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...fields, sort_order: sessions.length }),
          })
      if (!res.ok) {
        // Say why, rather than "could not save": the window is the usual reason.
        const body = (await res.json().catch(() => ({}))) as { code?: string }
        setError(
          body.code === 'GROUP_OUTSIDE_WINDOW'
            ? t('competitions.errorGroupOutsideWindow')
            : t('competitions.errorSaveFailed'),
        )
        return
      }
      resetForm()
      onRefresh()
    } finally {
      setSaving(false)
    }
  }

  async function removeSession(id: string) {
    const token = await getAccessTokenSilently()
    await fetch(`${apiUrl}/competition-sessions/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (id === editingId) resetForm()
    onRefresh()
  }

  const showForm = !locked && (formOpen || editingId !== null || sessions.length === 0)
  const sameDay = startsAt.slice(0, 10) === endsAt.slice(0, 10)
  // One day reads as times; a window across days needs its dates too.
  const windowLabel = (v: string) => (sameDay ? v.slice(11, 16) : v.slice(0, 16).replace('T', ' '))

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold">{t('competitions.tabGroups')}</h2>
          <p className="mt-1 text-sm text-foreground/60">
            {t('competitions.groupsHint')}{' '}
            {t('competitions.groupsWindowHint', {
              from: windowLabel(startsAt),
              to: windowLabel(endsAt),
            })}
          </p>
        </div>
        {!locked && !showForm && (
          <Button
            className="bg-foreground font-semibold text-background"
            onPress={() => {
              resetForm()
              setFormOpen(true)
            }}
          >
            + {t('competitions.addGroup')}
          </Button>
        )}
      </div>

      {sessions.length > 0 && (
        <SessionTimeline
          sessions={sessions}
          startsAt={startsAt}
          endsAt={endsAt}
          sameDay={sameDay}
        />
      )}

      {sessions.length === 0 ? (
        <p className="rounded-xl bg-content2 p-4 text-center text-sm text-foreground/60">
          {/* This is a real configuration, not an empty state to fix. */}
          {t('competitions.groupsEmptyMeansAllDay')}
        </p>
      ) : (
        <ul className="flex flex-col">
          {sessions.map((session) => (
            <li
              key={session.id}
              className={`flex items-center justify-between gap-4 border-t border-divider py-3 first:border-t-0 ${
                session.id === editingId ? 'text-primary' : ''
              }`}
            >
              <div className="min-w-0">
                <p className="font-semibold">{session.name}</p>
                <p className="text-sm text-foreground/60">
                  {session.starts_at.slice(11, 16)}–{session.ends_at.slice(11, 16)} ·{' '}
                  {session.capacity === null
                    ? t('competitions.groupUncapped')
                    : t('competitions.groupCapacity', { count: session.capacity })}
                </p>
              </div>
              {!locked && (
                <div className="flex shrink-0 gap-1">
                  <Button
                    size="sm"
                    variant="light"
                    color="primary"
                    onPress={() => startEditing(session)}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => void removeSession(session.id)}
                  >
                    {t('common.delete')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {locked && (
        <p className="rounded-lg bg-content2 p-3 text-sm text-foreground/60">
          {t('competitions.endedLockNote')}
        </p>
      )}

      {showForm && (
        <div className="flex flex-col gap-3 rounded-2xl bg-content2 p-5">
          <h3 className="text-base font-bold">
            {t(editingId ? 'competitions.editGroup' : 'competitions.addGroup')}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={t('competitions.groupName')}
              value={draft.name}
              onValueChange={(v) => setDraft((d) => ({ ...d, name: v }))}
              classNames={{ inputWrapper: 'bg-content1' }}
            />
            <Input
              type="number"
              min={1}
              label={t('competitions.groupCapacityLabel')}
              description={t('competitions.groupCapacityHint')}
              value={draft.capacity}
              onValueChange={(v) => setDraft((d) => ({ ...d, capacity: v }))}
              classNames={{ inputWrapper: 'bg-content1' }}
            />
            <DateField
              withTime
              label={t('competitions.fieldStartsAt')}
              min={bound(startsAt)}
              max={bound(endsAt)}
              value={draft.starts_at}
              onValueChange={(v) => setDraft((d) => ({ ...d, starts_at: v }))}
            />
            <DateField
              withTime
              label={t('competitions.fieldEndsAt')}
              min={bound(startsAt)}
              max={bound(endsAt)}
              value={draft.ends_at}
              onValueChange={(v) => setDraft((d) => ({ ...d, ends_at: v }))}
            />
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2">
            <Button
              className="bg-foreground font-semibold text-background"
              isLoading={saving}
              onPress={() => void saveSession()}
            >
              {t(editingId ? 'common.save' : 'competitions.addGroup')}
            </Button>
            {(editingId || (formOpen && sessions.length > 0)) && (
              <Button variant="light" onPress={resetForm}>
                {t('common.cancel')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Minutes since the epoch for a gym-local wall clock; only differences matter. */
function minutes(v: string): number {
  const [d, time = '00:00'] = v.split('T')
  const [y, m, day] = d.split('-').map(Number)
  const [h, mi] = time.split(':').map(Number)
  return Date.UTC(y, m - 1, day, h, mi) / 60000
}

const SHADES = [
  'bg-primary-100 text-primary-800',
  'bg-primary-200 text-primary-900',
  'bg-primary-300 text-primary-900',
]

/**
 * The competition window as one bar, each group a block sized to its hours and
 * the hours nobody is booked into marked free, so a gap or a clash shows at a
 * glance instead of being worked out from a list of times.
 */
function SessionTimeline({
  sessions,
  startsAt,
  endsAt,
  sameDay,
}: {
  sessions: CompetitionSession[]
  startsAt: string
  endsAt: string
  sameDay: boolean
}) {
  const { t } = useTranslation()
  const from = minutes(startsAt)
  const span = Math.max(minutes(endsAt) - from, 1)
  const pct = (v: string) => Math.min(Math.max(((minutes(v) - from) / span) * 100, 0), 100)
  const label = (v: string) => (sameDay ? v.slice(11, 16) : v.slice(5, 16).replace('T', ' '))

  const sorted = [...sessions].sort((a, b) => minutes(a.starts_at) - minutes(b.starts_at))
  // The free stretches: wherever the booked hours, merged, leave a gap.
  const free: { left: number; right: number }[] = []
  let cursor = 0
  for (const s of sorted) {
    const left = pct(s.starts_at)
    if (left > cursor + 0.5) free.push({ left: cursor, right: left })
    cursor = Math.max(cursor, pct(s.ends_at))
  }
  if (cursor < 99.5) free.push({ left: cursor, right: 100 })

  // Every start and every end, once each, left to right. A group's end is
  // as much a fact as its start. The window's own edges always show; any
  // other label that would sit on top of one already placed is dropped.
  const edges = [startsAt, endsAt]
  const inner = sorted
    .flatMap((s) => [s.starts_at, s.ends_at])
    .sort((a, b) => minutes(a) - minutes(b))
  const ticks: { at: number; text: string }[] = []
  for (const v of [...edges, ...inner]) {
    const at = pct(v)
    if (ticks.every((tick) => Math.abs(tick.at - at) > 7)) ticks.push({ at, text: label(v) })
  }
  ticks.sort((a, b) => a.at - b.at)

  return (
    <div
      className="rounded-2xl bg-content2 p-3"
      role="img"
      aria-label={t('competitionSetup.timelineLabel')}
    >
      <div className="relative h-10">
        {free.map((f) => (
          <div
            key={`free-${f.left}`}
            className="absolute inset-y-0 flex items-center justify-center rounded-lg bg-default-200 text-xs font-semibold text-foreground/60"
            style={{ left: `calc(${f.left}% + 2px)`, width: `calc(${f.right - f.left}% - 4px)` }}
          >
            {f.right - f.left > 8 && t('competitionSetup.timelineFree')}
          </div>
        ))}
        {sorted.map((s, i) => {
          const left = pct(s.starts_at)
          const width = pct(s.ends_at) - left
          return (
            <div
              key={s.id}
              className={`absolute inset-y-0 flex items-center justify-center overflow-hidden rounded-lg px-1 text-xs font-semibold ${SHADES[i % SHADES.length]}`}
              style={{ left: `calc(${left}% + 2px)`, width: `calc(${width}% - 4px)` }}
              title={`${s.name} · ${label(s.starts_at)}–${label(s.ends_at)}`}
            >
              <span className="truncate">{s.name}</span>
            </div>
          )
        })}
      </div>
      <div className="relative mt-1 h-4 text-xs text-foreground/60" aria-hidden="true">
        {ticks.map((tick, i) => (
          <span
            key={tick.at}
            className="absolute"
            style={
              i === ticks.length - 1 && tick.at > 90
                ? { right: 0 }
                : { left: `${tick.at}%`, transform: tick.at > 0 ? 'translateX(-50%)' : undefined }
            }
          >
            {tick.text}
          </span>
        ))}
      </div>
    </div>
  )
}
